import {
  Box,
  Button,
  Typography,
  Tab,
  Tabs,
  Grid,
  Divider,
  Paper,
  Alert,
} from "@mui/material";
import { useState, useMemo } from "react";
import TokenSwapInput from "../components/TokenSwapInput";
import { useDispatch, useSelector } from "react-redux";
import {
  BuyGameTokenAction,
  DepositTokenAction,
  WithdrawTokenAction,
  Reload,
} from "../store/user/actions";
import { AppState } from "../store";
import "../pages/Tools.css"; // Import wood-themed styling
import ForestIcon from "@mui/icons-material/Forest";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";

// Token type definition for better type safety
interface TokenBalance {
  token: string;
  walletBalance: number;
  inGameBalance: number;
  icon: string;
}

export const Exchange = () => {
  const dispatch = useDispatch<any>();
  const userModule = useSelector((state: AppState) => state.userModule);
  const { loginInfo } = userModule;

  // State management
  const [tabValue, setTabValue] = useState(0);
  const [activeTokenTab, setActiveTokenTab] = useState(0);
  const [swapAmount, setSwapAmount] = useState(0);
  const [depositAmount, setDepositAmount] = useState(0);
  const [withdrawAmount, setWithdrawAmount] = useState(0);

  // Constants
  const GOLD_WAX_RATE = 10;

  // Token data
  const tokens = useMemo<TokenBalance[]>(
    () => [
      {
        token: "GRASS",
        walletBalance: parseFloat(loginInfo?.balance?.grass_balance) || 0,
        inGameBalance: parseFloat(loginInfo?.balance?.game_grass) || 0,
        icon: "/images/token_grass.png",
      },
      {
        token: "ANTLERS",
        walletBalance: parseFloat(loginInfo?.balance?.antler_balance) || 0,
        inGameBalance: parseFloat(loginInfo?.balance?.game_antlers) || 0,
        icon: "/images/token_antlers.png",
      },
      {
        token: "GOLD",
        walletBalance: parseFloat(loginInfo?.balance?.gold_balance) || 0,
        inGameBalance: parseFloat(loginInfo?.balance?.game_gold) || 0,
        icon: "/images/token_gold.png",
      },
    ],
    [loginInfo?.balance]
  );

  // Computed values
  const waxBalance = parseFloat(loginInfo?.balance?.wax_balance) || 0;
  const goldBalance = parseFloat(loginInfo?.balance?.game_gold) || 0;
  const goldReceiveAmount = swapAmount * GOLD_WAX_RATE;
  const activeToken = tokens[activeTokenTab];

  // Input validation
  const validateNumericInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (value: number) => void
  ) => {
    const value = e.target.value;
    const regex = /^-?\d*\.?\d*$/;
    if (regex.test(value)) {
      setter(value === "" ? 0 : Number(value));
    }
  };

  // Handlers
  const handleSwap = () => {
    if (swapAmount <= 0 || swapAmount > waxBalance) return;
    dispatch(BuyGameTokenAction(swapAmount, goldReceiveAmount));
  };

  const handleDeposit = () => {
    if (depositAmount <= 0 || depositAmount > activeToken.walletBalance) return;
    dispatch(DepositTokenAction(depositAmount, activeToken.token));
    setDepositAmount(0);
  };

  const handleWithdraw = () => {
    if (withdrawAmount <= 0 || withdrawAmount > activeToken.inGameBalance)
      return;
    dispatch(WithdrawTokenAction(activeToken.token, withdrawAmount));
    dispatch(Reload());
  };

  const isSwapDisabled = swapAmount <= 0 || swapAmount > waxBalance;
  const isDepositDisabled =
    depositAmount <= 0 || depositAmount > activeToken.walletBalance;
  const isWithdrawDisabled =
    withdrawAmount <= 0 || withdrawAmount > activeToken.inGameBalance;

  return (
    <Box
      sx={{
        position: "relative", 
        minHeight: "calc(100vh - 76px)",
        background: "linear-gradient(135deg, #5c3c10 0%, #3d2c0a 100%)",
        padding: "24px",
        overflow: "hidden"
      }}
    >
      {/* Wood texture overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(45deg, rgba(92, 60, 16, 0.2) 25%, transparent 25%, transparent 50%, rgba(92, 60, 16, 0.2) 50%, rgba(92, 60, 16, 0.2) 75%, transparent 75%, transparent)",
          backgroundSize: "10px 10px",
          opacity: 0.3,
          zIndex: 0,
        }}
      />

      {/* Page Header */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 4, 
          background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
          borderRadius: '16px',
          position: 'relative', 
          overflow: 'hidden',
          border: '4px solid #7d5014',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
          zIndex: 1
        }}
      >
        <Box sx={{ 
          position: 'relative', 
          zIndex: 2, 
          display: 'flex', 
          flexDirection: {xs: 'column', sm: 'row'}, 
          alignItems: {xs: 'center', sm: 'flex-start'}, 
          justifyContent: 'space-between' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: {xs: 2, sm: 0} }}>
            <ForestIcon sx={{ color: "#f5e1a3", mr: 2, fontSize: "2.5rem" }} />
            <Box>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  color: "#f5e1a3", 
                  fontWeight: "bold",
                  textShadow: '2px 2px 4px rgba(0,0,0,0.6)'
                }}
              >
                Token Exchange
              </Typography>
              <Typography variant="body1" color="rgba(255,255,255,0.9)" sx={{ mt: 0.5, textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
                Swap, deposit and withdraw your tokens
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Wood grain overlay */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
          opacity: 0.4, 
          zIndex: 1 
        }} />
        
        {/* Decorative elements */}
        <Box sx={{ 
          position: 'absolute', 
          right: {xs: -30, md: 30}, 
          top: 0, 
          opacity: 0.15, 
          zIndex: 1, 
          transform: 'rotate(-10deg)' 
        }}>
          <ForestIcon sx={{ fontSize: 120 }} />
        </Box>
      </Paper>

      <Paper
        elevation={3}
        className="wood-panel"
        sx={{
          maxWidth: "800px",
          width: "100%",
          margin: "0 auto",
          border: "8px solid #5c3c10",
          borderRadius: "16px",
          position: "relative",
          background: "linear-gradient(135deg, #5c3c10 0%, #4a300b 100%)",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.3) 0px, rgba(92, 60, 16, 0.3) 2px, transparent 2px, transparent 4px)',
            opacity: 0.5,
            zIndex: 0,
            borderRadius: '8px'
          },
          '& > *': {
            position: 'relative',
            zIndex: 1
          }
        }}
      >
        <Grid container spacing={3} direction="column" alignItems="center" sx={{ p: 3 }}>
          <Grid item xs={12} width="100%">
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              variant="fullWidth"
              sx={{
                mb: 3,
                bgcolor: "rgba(0, 0, 0, 0.2)",
                borderRadius: "8px",
                width: "100%",
                border: '1px solid rgba(245, 225, 163, 0.2)'
              }}
              TabIndicatorProps={{
                style: { backgroundColor: "#f5e1a3" },
              }}
            >
              <Tab
                label="Swap WAX ↔ GOLD"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  transition: "all 0.3s ease",
                  "&.Mui-selected": { color: "#f5e1a3" },
                }}
              />
              <Tab
                label="Deposit & Withdraw"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  transition: "all 0.3s ease",
                  "&.Mui-selected": { color: "#f5e1a3" },
                }}
              />
            </Tabs>
          </Grid>

          <Grid item xs={12} sx={{ width: "100%" }}>
            <Box
              sx={{
                padding: { xs: 2, sm: 3 },
                bgcolor: "rgba(92, 60, 16, 0.3)",
                borderRadius: "12px",
                boxShadow: "0px 3px 10px rgba(0,0,0,0.3)",
                border: "1px solid rgba(245, 225, 163, 0.2)",
              }}
            >
              {tabValue === 0 ? (
                // WAX to GOLD Swap Tab
                <Box>
                  <Typography
                    variant="h6"
                    color="#f5e1a3"
                    align="center"
                    sx={{ mb: 1, fontWeight: "bold", textShadow: '1px 1px 2px rgba(0,0,0,0.6)' }}
                  >
                    WAX to GOLD Swap
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        bgcolor: "rgba(245, 225, 163, 0.2)",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.9rem",
                        color: "#f5e1a3",
                        fontWeight: "bold",
                        border: "1px solid rgba(245, 225, 163, 0.3)",
                      }}
                    >
                      1 WAX = {GOLD_WAX_RATE} GOLD
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TokenSwapInput
                        value={swapAmount}
                        onChange={(e: any) =>
                          validateNumericInput(e, setSwapAmount)
                        }
                        token={"WAX"}
                        img={"/images/token_wax.png"}
                        type="sell"
                        balance={`Wallet: ${waxBalance.toLocaleString()} WAX`}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: "50%",
                          bgcolor: "rgba(245, 225, 163, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ArrowDownwardIcon sx={{ color: "#f5e1a3" }} />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TokenSwapInput
                        value={goldReceiveAmount}
                        token={"GOLD"}
                        img={"/images/token_gold.png"}
                        type="receive"
                        balance={`In-Game: ${goldBalance.toLocaleString()} GOLD`}
                      />
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSwap}
                        disabled={isSwapDisabled}
                        size="large"
                        sx={{
                          fontWeight: "bold",
                          py: 1.5,
                          background: !isSwapDisabled
                            ? "linear-gradient(90deg, #7d5014 0%, #5c3c10 100%)"
                            : "rgba(0,0,0,0.3)",
                          color: "#f5e1a3",
                          transition: "all 0.3s ease",
                          border: "1px solid rgba(245, 225, 163, 0.3)",
                          "&:hover": {
                            background: !isSwapDisabled
                              ? "linear-gradient(90deg, #5c3c10 0%, #7d5014 100%)"
                              : "rgba(0,0,0,0.3)",
                          },
                        }}
                      >
                        SWAP WAX FOR GOLD
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box>
                  <Typography
                    variant="h6"
                    color="#f5e1a3"
                    align="center"
                    sx={{ fontWeight: "bold", mb: 2, textShadow: '1px 1px 2px rgba(0,0,0,0.6)' }}
                  >
                    Deposit & Withdraw Tokens
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Tabs
                      value={activeTokenTab}
                      onChange={(_, newValue) => {
                        setActiveTokenTab(newValue);
                        setDepositAmount(0);
                        setWithdrawAmount(0);
                      }}
                      variant="fullWidth"
                      sx={{
                        bgcolor: "rgba(0, 0, 0, 0.2)",
                        borderRadius: "8px",
                        mb: 3,
                        border: '1px solid rgba(245, 225, 163, 0.2)'
                      }}
                      TabIndicatorProps={{
                        style: { backgroundColor: "#f5e1a3" },
                      }}
                    >
                      {tokens.map((token, index) => (
                        <Tab
                          key={token.token}
                          label={token.token}
                          sx={{
                            color: "white",
                            fontWeight: "bold",
                            transition: "all 0.3s ease",
                            "&.Mui-selected": { color: "#f5e1a3" },
                          }}
                        />
                      ))}
                    </Tabs>
                  </Box>

                  <Box
                    sx={{
                      bgcolor: "rgba(0, 0, 0, 0.2)",
                      borderRadius: "10px",
                      p: 3,
                      border: "1px solid rgba(245, 225, 163, 0.2)",
                    }}
                  >
                    {/* Deposit Section */}
                    <Typography
                      variant="subtitle1"
                      color="#f5e1a3"
                      sx={{ fontWeight: "bold", mb: 1, textShadow: '1px 1px 2px rgba(0,0,0,0.6)' }}
                    >
                      Deposit {activeToken.token}
                    </Typography>

                    <TokenSwapInput
                      value={depositAmount}
                      onChange={(e: any) =>
                        validateNumericInput(e, setDepositAmount)
                      }
                      token={activeToken.token}
                      img={activeToken.icon}
                      type="deposit"
                      balance={`Wallet: ${activeToken.walletBalance.toLocaleString()} ${
                        activeToken.token
                      }`}
                    />

                    <Button
                      variant="contained"
                      onClick={handleDeposit}
                      disabled={isDepositDisabled}
                      fullWidth
                      sx={{
                        py: 1,
                        mt: 2,
                        mb: 3,
                        background: !isDepositDisabled
                          ? "linear-gradient(90deg, #7d5014 0%, #5c3c10 100%)"
                          : "rgba(0,0,0,0.3)",
                        color: "#f5e1a3",
                        transition: "all 0.3s ease",
                        border: "1px solid rgba(245, 225, 163, 0.3)",
                        "&:hover": {
                          background: !isDepositDisabled
                            ? "linear-gradient(90deg, #5c3c10 0%, #7d5014 100%)"
                            : "rgba(0,0,0,0.3)",
                        },
                      }}
                    >
                      Deposit {activeToken.token}
                    </Button>

                    <Divider sx={{ my: 3, bgcolor: "rgba(245, 225, 163, 0.2)" }} />

                    {/* Withdraw Section */}
                    <Typography
                      variant="subtitle1"
                      color="#f5e1a3"
                      sx={{ fontWeight: "bold", mb: 1, textShadow: '1px 1px 2px rgba(0,0,0,0.6)' }}
                    >
                      Withdraw {activeToken.token}
                    </Typography>

                    <TokenSwapInput
                      value={withdrawAmount}
                      onChange={(e: any) =>
                        validateNumericInput(e, setWithdrawAmount)
                      }
                      token={activeToken.token}
                      img={activeToken.icon}
                      type="withdraw"
                      balance={`In-Game: ${activeToken.inGameBalance.toLocaleString()} ${
                        activeToken.token
                      }`}
                    />

                    <Button
                      variant="contained"
                      onClick={handleWithdraw}
                      disabled={isWithdrawDisabled}
                      fullWidth
                      sx={{
                        py: 1,
                        mt: 2,
                        background: !isWithdrawDisabled
                          ? "linear-gradient(90deg, #7d5014 0%, #5c3c10 100%)"
                          : "rgba(0,0,0,0.3)",
                        color: "#f5e1a3",
                        transition: "all 0.3s ease",
                        border: "1px solid rgba(245, 225, 163, 0.3)",
                        "&:hover": {
                          background: !isWithdrawDisabled
                            ? "linear-gradient(90deg, #5c3c10 0%, #7d5014 100%)"
                            : "rgba(0,0,0,0.3)",
                        },
                      }}
                    >
                      Withdraw {activeToken.token}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Exchange;
